import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { getCustomerDetail } from '../../../services/CustomerService';
import { updateOrderDeliveryDetails } from '../../../services/CustomerService';

const DIGITS_ONLY = /^\d{10,15}$/;

export default function EditPhoneDialog({
  open,
  onClose,
  orderId,
  customerId,
  currentPhone,
  onSuccess,
  onError,
}) {
  const [tab, setTab] = useState(0);
  const [number, setNumber] = useState('');
  const [selectedPhoneId, setSelectedPhoneId] = useState('');
  const [phones, setPhones] = useState([]);
  const [loadingPhones, setLoadingPhones] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTab(0);
      setNumber(currentPhone ? currentPhone.replace(/\D/g, '') : '');
      setSelectedPhoneId('');
      setPhones([]);
    }
  }, [open, currentPhone]);

  useEffect(() => {
    if (open && tab === 1 && phones.length === 0 && customerId) {
      setLoadingPhones(true);
      getCustomerDetail(customerId)
        .then((detail) => {
          setPhones(detail?.phones || []);
        })
        .catch(() => {
          setPhones([]);
        })
        .finally(() => setLoadingPhones(false));
    }
  }, [open, tab, customerId, phones.length]);

  const handleSave = async () => {
    setSaving(true);
    try {
      let payload;
      if (tab === 0) {
        const digits = number.replace(/\D/g, '');
        payload = { phone: { number: digits } };
      } else {
        payload = { phone: { phone_id: Number(selectedPhoneId) } };
      }
      await updateOrderDeliveryDetails(orderId, payload);
      onSuccess('Phone updated successfully.');
      onClose();
    } catch (err) {
      onError(err?.message || 'Failed to update phone.');
    } finally {
      setSaving(false);
    }
  };

  const correctDigits = number.replace(/\D/g, '');
  const isCorrectValid = DIGITS_ONLY.test(correctDigits);
  const isSwapValid = !!selectedPhoneId;
  const canSave = tab === 0 ? isCorrectValid : isSwapValid;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit Phone Number</DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Correct number" />
          <Tab label="Saved phones" disabled={!customerId} />
        </Tabs>

        <Box mt={2}>
          {tab === 0 && (
            <>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Enter the corrected phone number (10-15 digits).
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Phone number"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                error={number.length > 0 && !isCorrectValid}
                helperText={
                  number.length > 0 && !isCorrectValid
                    ? 'Enter 10-15 digits'
                    : ''
                }
              />
            </>
          )}

          {tab === 1 && (
            <>
              {loadingPhones ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : (() => {
                const otherPhones = phones
                  .filter((p) => p && p.number)
                  .filter(
                    (p) =>
                      !currentPhone ||
                      p.number.replace(/\D/g, '') !==
                        currentPhone.replace(/\D/g, '')
                  );
                return otherPhones.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No other saved phone numbers available.
                  </Typography>
                ) : (
                  <>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Select a different phone from the customer's profile.
                    </Typography>
                    <RadioGroup
                      value={selectedPhoneId}
                      onChange={(e) => setSelectedPhoneId(e.target.value)}
                    >
                      {otherPhones.map((p) => (
                        <FormControlLabel
                          key={p.id}
                          value={String(p.id)}
                          control={<Radio size="small" color="primary" />}
                          label={p.number}
                        />
                      ))}
                    </RadioGroup>
                  </>
                );
              })()}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={!canSave || saving}
        >
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EditPhoneDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  orderId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  customerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currentPhone: PropTypes.string,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

EditPhoneDialog.defaultProps = {
  customerId: null,
  currentPhone: '',
};
